import { useCallback, useReducer, Key, Reducer } from 'react';

interface IQItem {
    id: React.Key;
}

interface IEnqueueAction<TQItem> {
    type: 'ENQUEUE';
    payload: TQItem[];
}

interface IDequeueAction {
    type: 'DEQUEUE';
    payload: Key[];
}

interface IClearAction {
    type: 'CLEAR';
    payload?: never;
}

type QueueActions<TQItem> = IEnqueueAction<TQItem> | IDequeueAction | IClearAction;

interface IQueueState<TQItem> {
    currentItem: TQItem | null;
    items: TQItem[];
}

function mergeItems<TQItem extends IQItem>(existingList: TQItem[], newList: TQItem[]) {

    const existingItemIds = existingList.map(i => i.id);
    const newItems = [...existingList];
    newList.forEach(i => {
        const idx = existingItemIds.indexOf(i.id);
        if (idx > -1) {
            newItems.splice(idx, 1, i);
            return;
        }
        newItems.push(i);
    });

    return newItems;
}

function reducer<TQItem extends IQItem>(state: IQueueState<TQItem>, action: QueueActions<TQItem>) {
    const newState: IQueueState<TQItem> = { ...state };
    switch (action.type) {
        case 'ENQUEUE':
            newState.items = mergeItems(state.items, action.payload);
            break;

        case 'DEQUEUE':
            newState.items = state.items.filter(i => !action.payload.includes(i.id));
            break;

        case 'CLEAR':
            newState.currentItem = null;
            newState.items = [];
            return newState;

        default:
            return state;
    }

    const { items: [firstItem = null] } = newState;
    newState.currentItem = firstItem;
    return newState;
}

export function useQueue<TQItem extends IQItem>(initialItems: TQItem[] = []) {
    const [{ currentItem }, dispatch] = useReducer<Reducer<IQueueState<TQItem>, QueueActions<TQItem>>>(reducer, { items: initialItems, currentItem: null });

    const enqueue = useCallback((...items: TQItem[]) => {
        dispatch({
            type: 'ENQUEUE',
            payload: items
        });
    }, []);

    const dequeue = useCallback((...ids: Key[]) => {
        dispatch({
            type: 'DEQUEUE',
            payload: ids
        });
    }, []);

    const clear = useCallback(() => {
        dispatch({
            type: 'CLEAR'
        });
    }, []);

    return { currentItem, enqueue, dequeue, clear };
}
